import type {
  HanziWordToGlossTypedQuestion,
  HanziWordToGlossTypedSkill,
  MistakeType,
  QuestionFlagType,
  UnsavedSkillRating,
} from "@/data/model";
import { MistakeKind, QuestionFlagKind, QuestionKind } from "@/data/model";
import {
  computeSkillRating,
  hanziWordFromSkill,
  hanziWordToGlossTyped,
} from "@/data/skills";
import { hanziFromHanziWord, loadDictionary } from "@/dictionary";
import { Rating } from "@/util/fsrs";
import { nonNullable } from "@pinyinly/lib/invariant";
import type { Mutable } from "@pinyinly/lib/types";

export async function hanziWordToGlossTypedQuestionOrThrow(
  skill: HanziWordToGlossTypedSkill,
  flag: QuestionFlagType | null,
): Promise<HanziWordToGlossTypedQuestion> {
  const hanziWord = hanziWordFromSkill(skill);
  const hanzi = hanziFromHanziWord(hanziWord);
  const dictionary = await loadDictionary();

  const bannedMeaningPrimaryGlossHint: Mutable<
    HanziWordToGlossTypedQuestion[`bannedMeaningPrimaryGlossHint`]
  > = [];
  const answers: HanziWordToGlossTypedQuestion[`answers`] = [];

  const previousHanziWords =
    flag?.kind === QuestionFlagKind.OtherAnswer
      ? flag.previousHanziWords
      : null;
  for (const [dictionaryHanziWord, dictionaryMeaning] of dictionary.lookupHanzi(
    hanzi,
  )) {
    if (previousHanziWords?.includes(dictionaryHanziWord) === true) {
      bannedMeaningPrimaryGlossHint.push(
        nonNullable(dictionaryMeaning.gloss[0]),
      );
    } else {
      answers.push({
        skill: hanziWordToGlossTyped(dictionaryHanziWord),
        glosses: dictionaryMeaning.gloss,
      });
    }
  }

  return {
    kind: QuestionKind.HanziWordToGlossTyped,
    answers,
    skill,
    bannedMeaningPrimaryGlossHint,
    flag,
  };
}

export type HanziToGlossTypedQuestionRating =
  | {
      correct: true;
      rating: Rating;
      skillRatings: UnsavedSkillRating[];
    }
  | {
      correct: false;
      rating: typeof Rating.Again;
      skillRatings: UnsavedSkillRating[];
      expectedAnswer: string;
      mistakes: MistakeType[];
    };

export function normalizeGlossForMatch(gloss: string): string {
  return (
    gloss
      .normalize(`NFKC`)
      .toLowerCase()
      // Treat apostrophe variants as optional so can't == cant.
      .replaceAll(/['’‘ʼ`´＇]/gu, ``)
      // Treat punctuation variants (e.g. parentheses, commas, trailing hyphen)
      // as optional separators.
      .replaceAll(/[\p{P}\p{S}]+/gu, ` `)
      .replaceAll(/^to (.+)/gu, `$1`) // Remove leading "to" for verbs.
      .replaceAll(/\s+/gu, ` `)
      .trim()
  );
}

/**
 * Determine if the user's answer is correct, and if not returning 1 or more
 * mistakes.
 */
export function gradeHanziToGlossTypedQuestion(
  question: HanziWordToGlossTypedQuestion,
  userGloss: string,
  durationMs: number,
): HanziToGlossTypedQuestionRating {
  const normalizedUserGloss = normalizeGlossForMatch(userGloss);

  // Look for a correct answer.
  for (const { skill, glosses } of question.answers) {
    const matched = glosses.some((gloss) => {
      if (gloss === userGloss) {
        return true;
      }

      const normalizedGloss = normalizeGlossForMatch(gloss);
      return normalizedGloss === normalizedUserGloss;
    });

    if (matched) {
      const correct = true;
      const skillRating = computeSkillRating({
        skill,
        correct,
        durationMs,
      });
      return {
        correct,
        rating: skillRating.rating,
        skillRatings: [skillRating],
      };
    }
  }

  // Report a mistake.
  {
    const correct = false;
    const expectedGloss = nonNullable(
      question.answers.find((a) => a.skill === question.skill)?.glosses[0],
    );
    return {
      correct,
      rating: Rating.Again,
      skillRatings: [
        computeSkillRating({
          skill: question.skill,
          correct,
          durationMs,
        }),
      ],
      expectedAnswer: expectedGloss,
      mistakes: [
        {
          kind: MistakeKind.HanziGloss,
          hanziOrHanziWord: hanziWordFromSkill(question.skill),
          gloss: userGloss,
        },
      ],
    };
  }
}
